import { LiveObject } from "@liveblocks/client";
import { useBatch } from "@liveblocks/react";
import type * as Stitches from "@stitches/react";
import React, { useCallback, useRef, useState } from "react";
import { GRID_SEAM } from "../consts";
import { colorValue } from "../css/color";
import { useMouseRef } from "../jotai/atoms";
import {
  updateBlockMap,
  useLiveBlockSubscription,
  useUserInfo,
} from "../liveblocks/maps";
import { config, styled } from "../stitches.config";
import { AnyBlock, Point2D, User } from "../types";
import { isValidMove } from "../ui/selection/useDragMove";
import { gridToPixelPosition, gridToPixelSize } from "../utils/gridPosition";
import { timestampToZIndex } from "../utils/updatedAt";
import BlockResizer from "./BlockResizer";
import CssBlock from "./CssBlock";
import FaceBlock from "./FaceBlock";
import ImageBlock from "./ImageBlock";
import ImageSearchBlock from "./ImageSearchBlock";
import { LinkBlock } from "./LinkBlock";
import MenuBlock from "./MenuBlock";
import SvgBlock from "./SvgBlock";
import JSONTextBlock, { MigrateTextBlockToJSON } from "./TextBlock";
import WebBlock from "./WebBlock";
import YoutubeBlock from "./YoutubeBlock";

// Dynamic imports are currently required wherever underlying code is incompatible with SSR
// in this case it is the agora sdk that is incompatible with SSR
import dynamic from "next/dynamic";
const ScreenBlock = dynamic(() => import("./ScreenBlock"), { ssr: false });

export type SelectionColor = Stitches.PropertyValue<
  "outlineColor",
  typeof config
>;

// Do not render blocks that are deleted
function BlockWrapper({
  liveBlock,
  upsertBlock,
  deleteBlock,
  selectionColor,
  justCreated,
}: {
  liveBlock: LiveObject<AnyBlock>;
  selectionColor?: SelectionColor;
  upsertBlock: (b: Partial<AnyBlock>, raise?: boolean) => void;
  deleteBlock: (blockId: string) => void;
  justCreated: boolean;
}) {
  const [block, blockClient] = useLiveBlockSubscription(liveBlock);
  if (!block || !blockClient || block.deleted) return null;
  return (
    <Block
      block={block}
      blockClient={blockClient}
      upsertBlock={upsertBlock}
      deleteBlock={deleteBlock}
      selectionColor={selectionColor}
      justCreated={justCreated}
    />
  );
}

function Block({
  upsertBlock,
  deleteBlock,
  selectionColor,
  justCreated,
  block,
  blockClient,
}: {
  upsertBlock: (b: Partial<AnyBlock>, raise?: boolean) => void;
  deleteBlock: (blockId: string) => void;
  selectionColor?: SelectionColor;
  justCreated: boolean;
  block: AnyBlock;
  blockClient: LiveObject<AnyBlock>;
}) {
  const mousePositionRef = useMouseRef();
  const [madeBy] = useUserInfo(block?.madeBy);
  const batch = useBatch();
  const updateBlock = useCallback(
    function (updates: Partial<AnyBlock>, raise = true) {
      if (blockClient) {
        batch(() => {
          updateBlockMap(blockClient, updates, raise);
        });
      }
    },
    [blockClient, batch]
  );
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const mouseOffsetInBlockRef = useRef<Point2D>({ x: 0, y: 0 });

  // Split makes json/text type into valid className
  const blockTypeClass = `block-${(block.type ?? "").split("/")[0]}`;
  const elementId = "block-" + block.id;

  // Select or Move block
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isValidMove(e)) return;

      if (!block || !mousePositionRef) {
        return;
      }
      setIsMoving(true);
      const blockPixelPos = gridToPixelPosition(block.position);
      mouseOffsetInBlockRef.current = {
        x: mousePositionRef.current.x - blockPixelPos.x,
        y: mousePositionRef.current.y - blockPixelPos.y,
      };
    },
    [block, mousePositionRef]
  );

  function onMouseUp() {
    if (isMoving) {
      setIsMoving(false);
    }
  }

  // FIXME: RS doesn't allow us to set all properties at once
  // so occasionally you may receive a new block with only some of its properties
  // RS is working on this, but it's tricky bc of transactions
  if (!block || !isBlockFullyFormed(block)) return <>Loading block...</>;

  const pos = gridToPixelPosition(block.position);
  const size = gridToPixelSize(block.size);
  const seamStyle = {
    transform: `translate(${pos.x}px, ${pos.y}px)`,
    zIndex: timestampToZIndex(block.updatedAt),
    width: `${size.width}px`,
    height: `${size.height}px`,
  };
  const transparent =
    (block.type === "image" && !!block.value) || block.type === "svg";

  const style = {
    width: "100%",
    height: "100%",
    outline: selectionColor ? `1px solid ${selectionColor}` : undefined,
    // overflow: visible is necessary for the textblock editor to render correctly
    // theoretically it doesn't make sense why it needs it, but it dows
    overflow: block.type ?? "" ? "visible" : "hidden",
    backgroundColor: transparent
      ? "transparent"
      : colorValue(madeBy?.color, 95),
  };
  const virtualCursorState = isMoving ? "grabbing" : "grab";
  return (
    <Seam style={seamStyle} date-block-id={block.id}>
      <BlockView
        className={`block ${blockTypeClass}  ${madeBy?.name}`}
        id={elementId}
        style={style}
        data-cursor={block.locked ? undefined : virtualCursorState}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        moving={isMoving}
        transparent={transparent}
        hoverable={!!block.locked}
        svg={block.type === "svg"}
      >
        <BlockFactory
          block={block}
          justCreated={justCreated}
          madeBy={madeBy}
          updateBlock={updateBlock}
          upsertBlock={upsertBlock}
          deleteBlock={deleteBlock}
        />
        {block.locked ? null : (
          <BlockResizer
            mousePositionRef={mousePositionRef}
            updateBlock={updateBlock}
            blockSize={block.size}
            blockPosition={block.position}
          />
        )}
      </BlockView>
    </Seam>
  );
}

export default React.memo(BlockWrapper);

const Seam = styled("li", {
  padding: GRID_SEAM / 2,
  position: "absolute",
  willChange: "transform",
});

export const BORDER_WIDTH = 0.5;

export const BlockView = styled("div", {
  height: "100%",
  width: "100%",
  display: "grid",
  pointerEvents: "auto", // remove hack later (see blocklist ul)
  transition: "filter 0.2s ",
  transitionTimingFunction: "cubic-bezier(0.32, 0.08, 0.24, 1.0)",
  fontSize: "$5",
  lineHeight: "1.5rem",
  position: "relative",
  overflow: "hidden",
  elevation: 1,
  outline: `${BORDER_WIDTH}px solid rgba(184, 162, 156, 0.1);`,
  borderRadius: "1px",
  variants: {
    hoverable: {
      true: {
        ["&:hover"]: {
          outline: "0.5px solid rgba(184, 162, 156, 0.2);",
        },
      },
    },
    transparent: {
      true: {
        elevation: 0,
        background: "transparent",
        border: "1px solid transparent",
        outline: "none",
      },
    },
    svg: {
      true: {
        pointerEvents: "none",
        border: "none",
        outline: "none",
        background: "transparent",
        filter: "none !important ",
      },
    },
    moving: {
      true: {
        elevation: 2,
      },
    },
  },
  compoundVariants: [
    {
      transparent: true,
      moving: true,
      css: {
        elevation: 2,
      },
    },
  ],
});

export function BlockFactory({
  block,
  updateBlock,
  upsertBlock,
  deleteBlock,
  justCreated,
  madeBy,
}: {
  block: AnyBlock;
  updateBlock: (block: Partial<AnyBlock>, raise?: boolean) => void;
  upsertBlock: (block: Partial<AnyBlock>) => void;
  deleteBlock: (blockId: string) => void;
  justCreated: boolean;
  madeBy?: User;
}) {
  switch (block.type) {
    case "web":
      return (
        <WebBlock
          block={block}
          updateBlock={updateBlock}
          upsertBlock={upsertBlock}
        />
      );
    case "face":
      return <FaceBlock block={block} updateBlock={updateBlock} />;
    case "text":
      return <MigrateTextBlockToJSON block={block} updateBlock={updateBlock} />;
    case "text/json":
      return (
        <JSONTextBlock
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
          deleteBlock={deleteBlock}
          upsertBlock={upsertBlock}
        />
      );
    case "imageSearch":
      return (
        <ImageSearchBlock
          block={block}
          justCreated={justCreated}
          updateBlock={updateBlock}
        />
      );
    case "image":
      return (
        <ImageBlock block={block} updateBlock={updateBlock} madeBy={madeBy} />
      );
    case "link":
      return <LinkBlock block={block} updateBlock={updateBlock} />;
    case "css":
      return (
        <CssBlock
          block={block}
          updateBlock={updateBlock}
          deleteBlock={deleteBlock}
        />
      );
    case "screen":
      return (
        <ScreenBlock
          block={block}
          updateBlock={updateBlock}
          upsertBlock={upsertBlock}
        />
      );
    case "youtube":
      return (
        <YoutubeBlock
          block={block}
          updateBlock={updateBlock}
          deleteBlock={deleteBlock}
        />
      );
    case "svg":
      return <SvgBlock block={block} />;
    case "menu":
      return <MenuBlock block={block} updateBlock={updateBlock} />;
    default:
      return null;
  }
}

function isBlockFullyFormed(block: Partial<AnyBlock>) {
  return (
    block.hasOwnProperty("id") &&
    block.hasOwnProperty("updatedAt") &&
    block.hasOwnProperty("position") &&
    block.hasOwnProperty("size")
  );
}
